import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Flag, CalendarBlank, Warning } from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { SCAMWATCH } from "../data/scamwatch";

const EASE = [0.16, 1, 0.3, 1];

export default function ScamWatchArticle() {
  const { slug } = useParams();
  const index = SCAMWATCH.findIndex((e) => e.slug === slug);
  const episode = SCAMWATCH[index];

  useEffect(() => {
    if (!episode) return;
    document.title = `${episode.title} — Red Flag Friday | The Timeshare Edit`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", episode.teaser);

    const origin = window.location.origin;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: episode.title,
      description: episode.teaser,
      datePublished: episode.date,
      inLanguage: "en",
      articleSection: "Red Flag Friday",
      mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/scamwatch/${episode.slug}` },
      author: { "@type": "Organization", name: "The Timeshare Edit", url: origin },
      publisher: { "@type": "Organization", name: "The Timeshare Edit", url: origin },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-scamwatch-schema", "true");
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => {
      el.remove();
      document.title = "The Timeshare Edit — Vacation Ownership Guidance";
    };
  }, [episode]);

  if (!episode) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-6 md:px-12 pt-48 pb-32" data-testid="scamwatch-not-found">
          <p className="font-serif text-4xl text-primary">Episode not found</p>
          <Link to="/" data-testid="scamwatch-not-found-home-link" className="inline-block mt-6 text-sm font-semibold text-accent border-b-2 border-accent pb-1">
            Back to the site
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const prev = SCAMWATCH[index - 1];
  const next = SCAMWATCH[index + 1];

  return (
    <div className="min-h-screen bg-background" data-testid="scamwatch-article-page">
      <Navbar />
      <main className="pt-[72px]">
        <header className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-28">
          <Reveal>
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); window.location.href = "/#scamwatch"; }}
              data-testid="scamwatch-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
            >
              <ArrowLeft size={16} weight="bold" /> Red Flag Friday
            </Link>
            <div className="mt-12 max-w-3xl">
              <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground" data-testid="scamwatch-meta">
                <span className="flex items-center gap-2 text-accent"><Flag size={15} weight="fill" /> Episode {episode.number}</span>
                <span className="flex items-center gap-2"><CalendarBlank size={15} weight="bold" /> {episode.date}</span>
              </div>
              <h1 className="font-serif text-primary tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mt-6" data-testid="scamwatch-title">
                {episode.title}
              </h1>
              <p className="mt-8 text-lg text-foreground/70 leading-relaxed" data-testid="scamwatch-teaser">
                {episode.teaser}
              </p>
            </div>
          </Reveal>
        </header>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16 md:mt-24 grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-7 lg:col-start-2" data-testid="scamwatch-body">
            {episode.sections.map((s, i) => (
              <Reveal key={i} className="mb-14">
                <h2 className="font-serif text-primary tracking-tight text-2xl sm:text-3xl leading-snug">{s.heading}</h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="mt-5 text-foreground/75 leading-[1.85]">{p}</p>
                ))}
              </Reveal>
            ))}
          </article>

          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-8">
              <Reveal delay={0.1} className="bg-secondary/50 border border-border rounded-sm p-7" data-testid="scamwatch-takeaways">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">If you remember nothing else</p>
                <ul className="mt-5 space-y-4">
                  {episode.takeaways.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-medium text-foreground/75 leading-relaxed">
                      <Warning size={16} weight="fill" className="text-accent shrink-0 mt-0.5" />
                      {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.2} className="bg-primary text-primary-foreground rounded-sm p-7" data-testid="scamwatch-cta-card">
                <p className="font-serif text-2xl leading-snug">Had a call like this?</p>
                <p className="mt-3 text-sm text-primary-foreground/70 leading-relaxed">
                  Send us the details. We'll tell you plainly what you're dealing with.
                </p>
                <Link
                  to="/"
                  onClick={(e) => { e.preventDefault(); window.location.href = "/#consult"; }}
                  data-testid="scamwatch-cta-consult"
                  className="group mt-6 inline-flex items-center gap-2 bg-accent text-accent-foreground text-sm font-semibold px-6 py-3 rounded-full hover:bg-background hover:text-primary transition-colors duration-300"
                >
                  Ask us about it
                  <ArrowRight size={15} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </aside>
        </div>

        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24 mb-4" data-testid="scamwatch-pagination">
          <div className="grid md:grid-cols-2 border-t border-border">
            {prev ? (
              <Link to={`/scamwatch/${prev.slug}`} data-testid="scamwatch-prev-link" className="group py-10 md:pr-12 border-b md:border-b-0 md:border-r border-border">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
                  <ArrowLeft size={14} weight="bold" className="transition-transform duration-300 group-hover:-translate-x-1" /> Newer episode
                </p>
                <p className="font-serif text-xl md:text-2xl text-primary mt-3 group-hover:text-accent transition-colors duration-300">{prev.title}</p>
              </Link>
            ) : <div className="hidden md:block border-r border-border" />}
            {next && (
              <Link to={`/scamwatch/${next.slug}`} data-testid="scamwatch-next-link" className="group py-10 md:pl-12 md:text-right">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2 md:justify-end">
                  Older episode <ArrowRight size={14} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </p>
                <p className="font-serif text-xl md:text-2xl text-primary mt-3 group-hover:text-accent transition-colors duration-300">{next.title}</p>
              </Link>
            )}
          </div>
        </nav>
      </main>
      <Footer />
    </div>
  );
}
